import { Component, Prop, Event, EventEmitter, Method, h } from '@stencil/core';

/**
 * An accordion simplifies the user interface by condensing long content into collapsible content sections.
 */
/**
 * @part accordion - This part is the full accordion container.
 * @part button - This part is the button that triggers toggling the accordion content.
 * @part button-open - This part is the button that triggers toggling the accordion content in the open state.
 * @part icon - This part is the icon next to the label.
 * @part arrow - This part is the arrow icon that indicates if the accordion is open or closed.
 * @part label - This part is the accordion label.
 */
@Component({
  tag: 'som-accordion',
  styleUrl: 'som-accordion.scss',
  shadow: true,
})
export class SomAccordion {
  collapseEl;
  buttonEl;

  /**
   * This property will be the accordion button label.
   */
  @Prop() label: string;

  /**
   * This boolean controls if the accordion is collapsed or expanded.
   */
  @Prop({ reflect: true, mutable: true }) collapsed: boolean = true;

  /**
   * The property is the name of an icon that will display before the headline.
   */
  @Prop() icon: string;

  /**
   * This event will be triggered any time the accordion is toggled.
   * @Returns `event.detail` will contain the current collapsed state of the accordion.
   */
  @Event({ bubbles: true, composed: true }) accordionToggled: EventEmitter<boolean>;

  /**
   * This method will toggle the collapse content. Toggle will show content if it is currently hidden or hide content if it is currently visibile.
   * @returns Promise will resolve after the toggle animation ends.
   */
  @Method()
  toggle(): Promise<void> {
    this.collapsed = !this.collapsed;
    return this.collapseEl.toggle();
  }

  /**
   * Show the collapse content regardless of the current state.
   * @returns Promise will resolve after the show animation ends.
   */
  @Method()
  show(): Promise<void> {
    this.collapsed = false;
    return this.collapseEl.show();
  }

  /**
   * This method hides content regardless of the current state.
   * @returns Promise will resolve after the hide animation ends.
   */

  @Method()
  hide(): Promise<void> {
    this.collapsed = true;
    return this.collapseEl.hide();
  }

  async toggleAccordion() {
    this.accordionToggled.emit(this.collapsed);
    await this.toggle();
  }

  render() {
    return (
      <div class="accordion" part="accordion">
        <button
          type="button"
          class={`accordion__button ${!this.collapsed ? 'accordion--open' : ''}`}
          aria-expanded={`${!this.collapsed}`}
          onClick={() => this.toggleAccordion()}
          ref={el => (this.buttonEl = el)}
          part={`button ${!this.collapsed ? 'button-open' : ''}`}
        >
          <div class="accordion__headline">
            {(this.icon) ? <span class="accordion__headline-icon" part="icon" innerHTML={`<som-icon name="${this.icon}" display="block"></som-icon>`}></span> : null }
            <span class="accordion__headline-label" part="label">{this.label}</span>
          </div>
          
          <div class="accordion__icon" part="arrow">
            {this.collapsed ? (
              <som-icon name="chevron-down" color="primary" display="block"></som-icon>
            ) : (
              <som-icon name="chevron-up" color="primary" display="block"></som-icon>
            )}
          </div>
        </button>
        <som-collapse collapsed={this.collapsed} ref={el => (this.collapseEl = el)}>
          <div class="accordion__content">
            <slot></slot>
          </div>
        </som-collapse>
      </div>
    );
  }
}
